<!--
  @component Wizard — multi-step modal flow primitive.
  tier:      primitive (visual atom + state-machine chrome)
  purpose:   shared shell for any multi-step modal flow (new
             project, add domain, etc.). Parent owns the step
             state + content; Wizard owns the chrome (overlay +
             header + step dots + footer buttons + Esc/back logic).

  CONTRACT
  ─────────────────────────────────────────────────────────────────
  Parent:                                          Wizard:
    • declares the step list (id + label)            • renders the dot indicator
    • owns currentStep $state                        • renders Back/Next/Finish
    • sets `canAdvance` based on validation          • disables Next when !canAdvance
    • provides {#snippet children(stepIndex)}        • passes stepIndex to children
    • implements onStepChange/onFinish/onCancel      • wires Esc → onCancel

  The Wizard never holds the form data — the parent does (so the
  parent can persist drafts, re-open on the same step, etc.). The
  Wizard is pure chrome.
-->
<script lang="ts" module>
  export interface WizardStepDef {
    readonly id: string;
    readonly label: string;
  }
</script>

<script lang="ts">
  import type { Snippet } from "svelte";

  interface Props {
    open: boolean;
    title: string;
    steps: readonly WizardStepDef[];
    currentStep: number;
    /** Validation for the current step — disables Next/Finish
     *  when false. Default true. */
    canAdvance?: boolean;
    /** Whether the wizard is in a saving state — disables all
     *  buttons + shows "…" on the finish button. */
    pending?: boolean;
    /** Label for the last step's action button. */
    finishLabel?: string;
    /** Marks the finish action destructive (error-red button). */
    destructive?: boolean;
    onStepChange: (next: number) => void;
    onFinish: () => void;
    onCancel: () => void;
    /** Content snippet — receives the current step index so the
     *  parent can switch its rendering. */
    children?: Snippet<[number]>;
  }
  const {
    open,
    title,
    steps,
    currentStep,
    canAdvance = true,
    pending = false,
    finishLabel = "Create",
    destructive = false,
    onStepChange,
    onFinish,
    onCancel,
    children,
  }: Props = $props();

  const totalSteps = $derived(steps.length);
  const isFirst = $derived(currentStep === 0);
  const isLast = $derived(currentStep === totalSteps - 1);

  function handleNext(): void {
    if (!canAdvance || pending) return;
    if (isLast) {
      onFinish();
    } else {
      onStepChange(currentStep + 1);
    }
  }
  function handleBack(): void {
    if (pending || isFirst) return;
    onStepChange(currentStep - 1);
  }
  function onKey(e: KeyboardEvent): void {
    if (!open) return;
    if (e.key === "Escape") onCancel();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div class="overlay" role="dialog" aria-modal="true" aria-label={title}>
    <div class="wizard">
      <header>
        <h2>{title}</h2>
        <ol class="dots" aria-label="Step progress">
          {#each steps as s, i (s.id)}
            <li
              class="dot"
              data-state={i < currentStep ? "done" : i === currentStep ? "current" : "todo"}
            >
              <span class="dot-num" aria-hidden="true">
                {i < currentStep ? "✓" : i + 1}
              </span>
              <span class="dot-label">{s.label}</span>
            </li>
          {/each}
        </ol>
      </header>

      <section class="body">
        {@render children?.(currentStep)}
      </section>

      <footer>
        <button type="button" class="ghost" onclick={onCancel} disabled={pending}>
          Cancel
        </button>
        <div class="spacer"></div>
        {#if !isFirst}
          <button type="button" onclick={handleBack} disabled={pending}>
            Back
          </button>
        {/if}
        <button
          type="button"
          class="primary"
          class:destructive
          onclick={handleNext}
          disabled={!canAdvance || pending}
        >
          {pending ? "…" : isLast ? finishLabel : "Next"}
        </button>
      </footer>
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: var(--surface-overlay);
    backdrop-filter: var(--glass-blur);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: var(--space-4);
  }
  .wizard {
    background: var(--surface-raised);
    border: 0.5px solid var(--border-strong);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    max-width: 600px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    font-family: var(--type-font);
  }
  header {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-5);
    border-bottom: 0.5px solid var(--border);
  }
  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }
  .dots {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: var(--space-1, 4px);
    align-items: center;
  }
  .dot {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--text-tertiary);
    font-size: 11px;
  }
  .dot:not(:last-child)::after {
    content: "";
    display: inline-block;
    width: 24px;
    height: 1px;
    background: var(--border);
    margin: 0 var(--space-2);
  }
  .dot[data-state="done"] {
    color: var(--success);
  }
  .dot[data-state="done"]:not(:last-child)::after {
    background: var(--success);
    opacity: 0.6;
  }
  .dot[data-state="current"] {
    color: var(--accent-text);
  }
  .dot-num {
    width: 22px;
    height: 22px;
    border-radius: var(--radius-pill);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
  }
  .dot[data-state="done"] .dot-num {
    background: var(--success-subtle);
    border-color: var(--success-subtle);
  }
  .dot[data-state="current"] .dot-num {
    background: var(--accent-subtle);
    border-color: var(--accent-border);
    box-shadow: 0 0 0 3px var(--accent-glow);
  }
  .dot-label {
    text-transform: lowercase;
  }
  .body {
    flex: 1;
    padding: var(--space-5);
    overflow: auto;
    color: var(--text-primary);
  }
  footer {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-4) var(--space-5);
    border-top: 0.5px solid var(--border);
  }
  .spacer { flex: 1; }
  button {
    appearance: none;
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font: inherit;
    font-size: 12px;
    padding: var(--space-2) var(--space-4);
    cursor: pointer;
    transition: background var(--ease-fast);
  }
  button:hover:not(:disabled) {
    background: var(--bg-hover);
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  button.ghost {
    background: transparent;
  }
  button.primary {
    background: var(--accent-subtle);
    color: var(--accent-text);
    border-color: var(--accent-border);
  }
  button.primary.destructive {
    background: var(--error-subtle);
    color: var(--error);
    border-color: var(--error-subtle);
  }
</style>
